import { useQuery } from "@tanstack/react-query";
import { showcaseClient } from "@/integrations/supabase/showcase-client";
import { isUUID } from "@/utils/slugify";

interface PublicDevice {
  device_id: string;
  device_name: string;
  device_description?: string | null;
  device_last_updated: string;
  lat_wgs84: number | null;
  long_wgs84: number | null;
  past_images: any[];
  imageKit?: {
    thumbnailUrl?: string;
    fullUrl?: string;
  };
}

export const usePublicOrganizationDevices = (organizationId: string) => {
  console.log('========================================');
  console.log('🔍 [usePublicOrganizationDevices] HOOK INITIALIZATION');
  console.log('🔍 [usePublicOrganizationDevices] Organization ID:', organizationId);
  console.log('🔍 [usePublicOrganizationDevices] Organization ID type:', typeof organizationId);
  console.log('🔍 [usePublicOrganizationDevices] Showcase client exists:', !!showcaseClient);
  console.log('🔍 [usePublicOrganizationDevices] Current route:', window.location.pathname);
  console.log('🔍 [usePublicOrganizationDevices] Timestamp:', new Date().toISOString());
  console.log('========================================');
  
  return useQuery({
    queryKey: ['public-organization-devices', organizationId],
    queryFn: async (): Promise<{ organization: any; devices: PublicDevice[] }> => {
      try {
        console.log('🔍 [PublicOrgDevices] ========== QUERY STARTING ==========');
        console.log('🔍 [PublicOrgDevices] Organization ID:', organizationId);
        console.log('🔍 [PublicOrgDevices] Timestamp:', new Date().toISOString());
        
        // First, verify the organization is public
        // Support both UUID and slug lookup
        const lookupByUUID = isUUID(organizationId);
        console.log('🔍 [PublicOrgDevices] Step 1: Fetching organization...');
        console.log('🔍 [PublicOrgDevices] Lookup method:', lookupByUUID ? 'UUID' : 'Slug');
        
        const { data: organization, error: orgError } = await showcaseClient
          .from('organizations')
          .select('id, name, description, logo_url, website, address, visibility, marketing_banner_url, slug, showcase_access_code, showcase_access_code_enabled')
          .eq(lookupByUUID ? 'id' : 'slug', organizationId)
          .eq('visibility', 'public')
          .single();

        console.log('🔍 [PublicOrgDevices] Organization query result:', { 
          hasData: !!organization, 
          hasError: !!orgError,
          organization, 
          orgError: orgError ? { message: orgError.message, code: orgError.code, details: orgError.details } : null
        });

        if (orgError || !organization) {
          console.error('[PublicOrgDevices] Organization error:', orgError);
          throw new Error(`Organization not found or not public: ${orgError?.message || 'Unknown error'}`);
        }

        // Get all users in the organization
        const { data: orgUsers, error: usersError } = await showcaseClient
          .from('user_roles')
          .select('user_id')
          .eq('organization_id', organization.id);

        console.log('[PublicOrgDevices] User roles query result:', { orgUsers, usersError });

        if (usersError) {
          console.error('[PublicOrgDevices] Error fetching organization users:', usersError);
          throw new Error(`Failed to fetch organization users: ${usersError.message}`);
        }

        if (!orgUsers || orgUsers.length === 0) {
          console.log('[PublicOrgDevices] No users found for organization');
          return { organization, devices: [] };
        }

        const userIds = orgUsers.map(u => u.user_id);
        console.log('[PublicOrgDevices] User IDs:', userIds);

        // Get device assignments for all organization users
        const { data: assignments, error: assignmentsError } = await showcaseClient
          .from('device_assignments')
          .select('device_id')
          .in('user_id', userIds);

        console.log('[PublicOrgDevices] Device assignments query result:', { assignments, assignmentsError });

        if (assignmentsError) {
          console.error('[PublicOrgDevices] Error fetching device assignments:', assignmentsError);
          throw new Error(`Failed to fetch device assignments: ${assignmentsError.message}`);
        }

        if (!assignments || assignments.length === 0) {
          console.log('[PublicOrgDevices] No device assignments found');
          return { organization, devices: [] };
        }

        const deviceIds = [...new Set(assignments.map(a => a.device_id))];
        console.log('[PublicOrgDevices] Device IDs:', deviceIds);

        // Get device details for public devices only
        const { data: devices, error: devicesError } = await showcaseClient
          .from('device_details')
          .select('device_id, device_name, device_description, device_last_updated, lat_wgs84, long_wgs84, location, past_images, imagekit')
          .in('device_id', deviceIds)
          .eq('enable_public_view', true)
          .order('device_name');

        console.log('[PublicOrgDevices] Device details query result:', { devices, devicesError });

        if (devicesError) {
          console.error('[PublicOrgDevices] Error fetching device details:', devicesError);
          throw new Error(`Failed to fetch device details: ${devicesError.message}`);
        }

        // Some devices only carry coordinates inside the processed `location` object
        // (same fallback the internal map uses), so resolve both sources here.
        const parseCoordinate = (value: unknown): number | null => {
          if (value === null || value === undefined || value === '') return null;
          const parsed = typeof value === 'number' ? value : parseFloat(String(value));
          return Number.isFinite(parsed) ? parsed : null;
        };

        const result = { 
          organization, 
          devices: (devices || []).map(d => {
            const imagekit = d.imagekit as { thumbnailUrl?: string; url?: string } | null;
            const location = d.location as { latWGS84?: number | string; longWGS84?: number | string } | null;
            return {
              ...d,
              lat_wgs84: parseCoordinate(d.lat_wgs84) ?? parseCoordinate(location?.latWGS84),
              long_wgs84: parseCoordinate(d.long_wgs84) ?? parseCoordinate(location?.longWGS84),
              imageKit: imagekit ? {
                thumbnailUrl: imagekit.thumbnailUrl,
                fullUrl: imagekit.url || imagekit.thumbnailUrl
              } : undefined
            };
          })
        };
        
        console.log('[PublicOrgDevices] Final result with mapped imageKit:', result);
        return result;
      } catch (error) {
        console.error('[PublicOrgDevices] Fatal error:', error);
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!organizationId,
  });
};
